import {createRouter, createWebHistory} from 'vue-router'
import store from '@/store'

// Layouts
import MainLayout from '@/layouts/DefaultLayout.vue'

// Import route modules
import rolesRoutes from './routes/roles'
import usersRoutes from './routes/users'
import sitesRoutes from './routes/sites'
import studentsRoutes from './routes/student-routes'
import guardiansRoutes from './routes/guardian-routes'
import vendorRoutes from './routes/vendor-routes'
import siteSchedules from "@/router/routes/site-schedules";
import mealTypesRoutes from "./routes/meal-types";
import menuRoutes from "./routes/menu-routes";
import mealCountRoutes from "@/router/meal-count-routes";
import deliverySlipRoutes from "./routes/delivery-slip-routes";
import organizationRoutes from "./routes/organization-routes";

// Define routes
const routes = [
    // Public landing page
    {
        path: '/',
        name: 'home',
        component: () => import('@/views/LandingPage.vue'),
        meta: {
            title: 'CACFP - Child and Adult Care Food Program',
            public: true,
            requiresAuth: false
        }
    },

    // Public About page
    {
        path: '/about',
        name: 'about',
        component: () => import('@/views/AboutPage.vue'),
        meta: {
            title: 'About Us - CACFP',
            public: true,
            requiresAuth: false
        }
    },

    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/auth/LoginPage.vue'),
        meta: {
            title: 'Login - CACFP',
            public: true,
            requiresAuth: false
        }
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => import('@/views/auth/SignupPage.vue'),
        meta: {
            title: 'Sign Up - CACFP',
            public: true,
            requiresAuth: false
        }
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordPage.vue'),
        meta: {
            title: 'Forgot Password - CACFP',
            public: true,
            requiresAuth: false
        }
    },
    // Public Support page
    {
        path: '/support',
        name: 'support',
        component: () => import('@/views/SupportPage.vue'),
        meta: {
            title: 'Support & Contact - CACFP',
            public: true,
            requiresAuth: false
        }
    },
    
    // Protected dashboard and app routes
    {
        path: '/dashboard',
        component: MainLayout,
        meta: {requiresAuth: true},
        children: [
            ...rolesRoutes,
            ...usersRoutes,
            ...sitesRoutes,
            ...studentsRoutes,
            ...guardiansRoutes,
            ...vendorRoutes,
            ...siteSchedules,
            ...mealTypesRoutes,
            ...menuRoutes,
            ...mealCountRoutes,
            ...deliverySlipRoutes,
            ...organizationRoutes,
            {
                path: '',
                name: 'dashboard',
                component: () => import('@/pages/DashboardPage.vue'),
                meta: {
                    title: 'Dashboard',
                    roles: ['sponsor', 'site_representative','vendor']
                }
            },
            {
                path: '/account',
                name: 'account',
                component: () => import('@/pages/AccountPage.vue'),
                meta: {
                    title: 'Account Details',
                    requiresAuth: true,
                    roles: ['sponsor','vendor']
                }
            },
            {
                path: '/profile',
                name: 'profile',
                component: () => import('@/views/ProfilePage.vue'),
                meta: {
                    title: 'My Profile',
                    requiresAuth: true
                }
            },
            {
                path: '/settings',
                name: 'settings',
                component: () => import('@/views/SettingsPage.vue'),
                meta: {
                    title: 'Settings',
                    requiresAuth: true
                }
            },
            {
                path: '/activities',
                name: 'activities',
                component: ()=>import('@/views/activities/ActivityLogPage.vue'),
                meta: { requiresAuth: true }
            },

            // Meal Tally routes
            {
              path: '/meal-tally',
              name: 'MealTally',
              component:()=>import('@/pages/MealTallyPage.vue'),
              meta: { 
                requiresAuth: true,
                roles: ['sponsor', 'site_representative'] // Both can access meal tally
              }
            },
            {
              path: '/meal-tally/sessions/:id',
              name: 'MealTallySessionDetails',
              component: () => import('@/pages/MealTallySessionDetailsPage.vue'),
              meta: { 
                requiresAuth: true,
                roles: ['sponsor', 'site_representative'] // Both can view session details
              }
            },
            {
              path: '/site-menus/:id/view',
              name: 'site-menu-view',
              component: () => import('../views/menu/SiteMenuView.vue'),
              meta: { requiresAuth: true }
            },
            // Menu routes
            // {
            //     path: '/menus',
            //     name: 'menus',
            //     component: () => import('@/views/menu/MenusPage.vue'),
            //     meta: {
            //         requiresAuth: true,
            //         title: 'Menus'
            //     }
            // },
            {
                path: '/site-menus',
                name: 'site-menus',
                component: () => import('@/views/menu/SiteMenusPage.vue'),
                meta: {
                    requiresAuth: true,
                    title: 'Site Menus',
                    roles: ['sponsor', 'site_representative','vendor']
                }
            },

        ]
    },

    {
        path: '/dev/routes',
        name: 'dev.routes',
        component: () => import('@/pages/RoutesConfigHelper.vue'),
        meta: {title: 'Routes Config Helper', requiresAuth: true, permissions: ['developer']}
    },
    // Public authentication routes

    {
        path: '/forbidden',
        name: 'forbidden',
        component: () => import('@/views/ForbiddenPage.vue'),
        meta: {
            title: 'Access Denied',
            public: true,
            requiresAuth: false
        }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundPage.vue'),
        meta: {
            title: 'Page Not Found',
            public: true,
            requiresAuth: false
        }
    }
]

// Create router instance
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return {top: 0}
        }
    }
})

// Permission checker import (currently disabled)
// import {canAccessRoute} from '@/utils/permissions';

// Navigation guards
router.beforeEach(async (to, from, next) => {
    // Set document title
    setDocumentTitle(to);

    // Define public routes that don't require any authentication
    const publicRoutes = ['home', 'about', 'support', 'login', 'forgot-password', 'forbidden', 'not-found'];
    const isLoggedIn = store.getters['auth/isLoggedIn'];

    // Handle login/forgot-password - redirect authenticated users to dashboard
    if (['login', 'forgot-password'].includes(to.name) && isLoggedIn) {
        try {
            const isValid = await store.dispatch('auth/checkToken');
            if (isValid) {
                return next('/dashboard');
            }
        } catch (error) {
            console.warn('Token validation failed during navigation');
            // Continue to auth page since token is now invalid
        }
    }

    // Allow immediate access to all public routes
    if (publicRoutes.includes(to.name)) {
        return next();
    }

    // Check if the route requires authentication
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    // Handle authentication requirements for protected routes
    if (requiresAuth && !isLoggedIn) {
        return handleUnauthenticatedAccess(to, next);
    } else if (requiresAuth && isLoggedIn) {
        // Check permissions for authenticated routes
        if (!await checkRoutePermissions(to, next)) {
            return; // Permission check failed, navigation handled in the function
        }
    }

    return next();
})

/**
 * Set document title based on route metadata
 * @param {Object} route - Vue route object
 */
function setDocumentTitle(route) {
    document.title = route.meta.title
        ? `${route.meta.title} | ${process.env.VUE_APP_TITLE}`
        : process.env.VUE_APP_TITLE;
}


/**
 * Save intended destination and redirect to login
 * @param {Object} route - Vue route object
 * @param {Function} next - Vue Router next function
 */
function handleUnauthenticatedAccess(route, next) {
    if (route.path !== '/login') {
        // Save the location we were trying to access
        sessionStorage.setItem('redirectAfterLogin', route.fullPath);
    }
    return next('/login');
}

/**
 * Check if user has permission to access route
 * @param {Object} route - Vue route object
 * @param {Function} next - Vue Router next function
 * @returns {boolean} - Whether the user has permission
 */
async function checkRoutePermissions() {
    // Permission checking disabled - always allow access for authenticated users
    return true;
}

export default router
